// Normalizes user-selected images before upload so they pass the Cloudflare
// worker limits (10 MB max, content-type allowlist of gif/jpeg/png/webp).
// Files that are already an allowed type and small enough are returned
// unchanged; everything else (HEIC, oversized photos, unknown types) is
// decoded, downscaled, and re-encoded as JPEG.

const SIZE_LIMIT_BYTES = 9 * 1024 * 1024;
const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.85;
const PASSTHROUGH_TYPES = ['image/gif', 'image/jpeg', 'image/png', 'image/webp'];

const createUnsupportedImageError = (cause) => {
  const error = new Error('This image format is not supported by your browser');
  error.code = 'UNSUPPORTED_IMAGE';
  if (cause) {
    error.cause = cause;
  }
  return error;
};

const decodeWithImageElement = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(createUnsupportedImageError());
    };
    image.src = objectUrl;
  });

const decodeImage = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // createImageBitmap can't read this file, fall back to an Image element
    }
  }
  return decodeWithImageElement(file);
};

const canvasToJpegBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(createUnsupportedImageError())),
      'image/jpeg',
      JPEG_QUALITY
    );
  });

const toJpegFilename = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  const base = lastDot > 0 ? filename.slice(0, lastDot) : filename;
  return base + '.jpg';
};

export const prepareImageForUpload = async (file) => {
  // gif kept as-is to preserve animation, jpeg/png/webp kept as-is to avoid
  // a needless re-encode, as long as they fit under the worker upload limit
  if (PASSTHROUGH_TYPES.includes(file.type) && file.size <= SIZE_LIMIT_BYTES) {
    return file;
  }

  let source;
  try {
    source = await decodeImage(file);
  } catch (error) {
    throw error?.code === 'UNSUPPORTED_IMAGE'
      ? error
      : createUnsupportedImageError(error);
  }

  const sourceWidth = source.naturalWidth || source.width;
  const sourceHeight = source.naturalHeight || source.height;
  //downscale so the longest side fits within MAX_DIMENSION, never upscale
  const scale = Math.min(1, MAX_DIMENSION / Math.max(sourceWidth, sourceHeight));
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    throw createUnsupportedImageError();
  }
  context.drawImage(source, 0, 0, targetWidth, targetHeight);
  if (typeof source.close === 'function') {
    source.close();
  }

  const blob = await canvasToJpegBlob(canvas);
  return new File([blob], toJpegFilename(file.name || 'image'), {
    type: 'image/jpeg',
  });
};
